import { getQueueSessions } from '@api/queries/getQueueSessions';
import IAppLogger from '@app/lib/IAppLogger';
import appManager from '@app/appManager';
import { serviceState } from './state';

type QueueSyncItem = {
  queueId: string;
  status: 'pending' | 'syncing';
};

type SyncConfig = {
  maxConcurrent: number;
  staggerDelayMs: number;
  batchCooldownMs: number; // Cooldown between batches during sync
  syncCooldownMs: number; // Cooldown after completing full sync cycle
  failureCountThreshold: number; // Failed items per cycle required to trip cooldown
  initialBackoffMs: number; // First cooldown duration
  backoffMultiplier: number; // Multiplier applied per consecutive failed cycle
  maxBackoffMs: number; // Cap on cooldown duration (circuit-open behavior at cap)
};

const DEFAULT_CONFIG: SyncConfig = {
  maxConcurrent: 5,
  staggerDelayMs: 200,
  batchCooldownMs: 1_000, // 1 second cooldown between batches during sync
  syncCooldownMs: 5 * 60 * 1_000, // 5 minutes cooldown after completing sync cycle
  failureCountThreshold: 3,
  initialBackoffMs: 10 * 60 * 1_000, // 10 minutes
  backoffMultiplier: 2,
  maxBackoffMs: 120 * 60 * 1_000, // 120 minutes
};

/**
 * QueueSessionSyncManager
 *
 * Manages periodic synchronization of queue sessions from PCS API.
 * Lives within ServiceModule and is mounted/unmounted by the workspace
 * lifecycle (openWorkspace/closeWorkspace) so it only runs when the
 * workspace is properly connected.
 *
 * Key behaviors:
 * - checkAndEnqueue() tracks out-of-sync queues (called from setQueueStatus)
 * - mount() starts periodic sync loop (every 5 minutes by default)
 * - Each sync cycle: syncs all out-of-sync queues in batches (snapshot at entry)
 * - unmount() stops periodic sync and aborts in-flight requests
 * - Throttling: max 5 concurrent, 200ms stagger within batch, 1s between batches, 5min between cycles
 * - Apollo retryLink handles request-level retries (5 attempts); no in-manager retry.
 */
export class QueueSessionSyncManager {
  private readonly syncQueue: Map<string, QueueSyncItem> = new Map();
  private processing = false;
  private readonly config: SyncConfig;
  private abortController: AbortController | null = null;
  private enabled = false;
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly logger: IAppLogger;
  private cooldownUntilMs: number | null = null;
  private consecutiveFailedCycles = 0;
  private cycleAttempted: Set<string> = new Set();
  private cycleFailed: Set<string> = new Set();
  private lastCycleStats: {
    attempted: number;
    succeeded: number;
    failed: number;
    durationMs: number;
  } | null = null;

  constructor(config: Partial<SyncConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = appManager.logger('QueueSessionSyncManager');
  }

  /**
   * Called by setQueueStatus when mismatch detected.
   * Tracks out-of-sync queues but does NOT trigger immediate processing.
   * Processing happens periodically via the sync interval.
   */
  checkAndEnqueue(
    queueId: string,
    activeSessionCount: number,
    currentSessionsLength: number,
  ): void {
    // Queue is in sync - remove from tracking
    if (activeSessionCount === currentSessionsLength) {
      const wasTracked = this.syncQueue.has(queueId);
      this.syncQueue.delete(queueId);

      if (wasTracked) {
        this.logger.debug(
          '[QueueSessionSyncManager] Queue now in sync, removed from tracking',
          {
            queueId,
            activeSessionCount,
            currentSessionsLength,
          },
        );
      }
      return;
    }

    // Already syncing this queue - don't interfere
    const existing = this.syncQueue.get(queueId);
    if (existing?.status === 'syncing') {
      this.logger.debug(
        '[QueueSessionSyncManager] Queue already syncing, skipping enqueue',
        {
          queueId,
        },
      );
      return;
    }

    // Add/update in sync queue (always, regardless of enabled state)
    // Periodic sync will process this later
    const isNew = !existing;
    this.syncQueue.set(queueId, {
      queueId,
      status: 'pending',
    });

    this.logger.debug('[QueueSessionSyncManager] Queue mismatch detected', {
      queueId,
      activeSessionCount,
      currentSessionsLength,
      action: isNew ? 'added_to_queue' : 'updated_in_queue',
      totalQueueSize: this.syncQueue.size,
    });
  }

  /**
   * Called when the workspace connects. Starts periodic sync loop.
   */
  mount(): void {
    // Prevent duplicate intervals if already mounted
    if (this.syncIntervalId) {
      this.logger.debug(
        '[QueueSessionSyncManager] Already mounted, ignoring duplicate mount() call',
      );
      return;
    }

    this.enabled = true;

    this.logger.debug(
      '[QueueSessionSyncManager] Mounted - starting periodic sync',
      {
        syncIntervalSeconds: this.config.syncCooldownMs / 1000,
      },
    );

    // Initial check: enqueue any queues already out of sync at mount time
    for (const queue of serviceState.queues) {
      this.checkAndEnqueue(
        queue.id,
        queue.status.activeSessionCount ?? 0,
        queue.sessions.length,
      );
    }

    // Sync immediately on mount if there are out-of-sync queues (and not on cooldown)
    if (this.syncQueue.size > 0) {
      if (this.isOnCooldown()) {
        this.logger.debug(
          '[QueueSessionSyncManager] Initial sync skipped on mount - on cooldown',
          {
            cooldownUntilMs: this.cooldownUntilMs,
            consecutiveFailedCycles: this.consecutiveFailedCycles,
            outOfSyncQueueCount: this.syncQueue.size,
          },
        );
      } else {
        this.logger.debug('[QueueSessionSyncManager] Starting initial sync', {
          outOfSyncQueueCount: this.syncQueue.size,
        });
        void this.runSyncCycle();
      }
    } else {
      this.logger.debug(
        '[QueueSessionSyncManager] No out-of-sync queues on mount, waiting for mismatches',
      );
    }

    // Start periodic sync interval
    this.syncIntervalId = setInterval(() => {
      this.logger.debug(
        '[QueueSessionSyncManager] Periodic sync interval triggered',
        {
          enabled: this.enabled,
          processing: this.processing,
          queueSize: this.syncQueue.size,
        },
      );

      if (this.enabled && !this.processing) {
        void this.runSyncCycle();
      }
    }, this.config.syncCooldownMs);
  }

  /**
   * Called when the workspace disconnects. Stops periodic sync, aborts in-flight syncs.
   * Note: Does NOT clear the queue - mismatches continue to be tracked.
   */
  unmount(): void {
    this.enabled = false;
    this.abortController?.abort();

    // Stop periodic sync interval
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
    // Don't set processing = false here - let processSyncQueue's finally block handle it

    this.logger.debug(
      '[QueueSessionSyncManager] Unmounted - stopped periodic sync',
      {
        remainingQueuedItems: this.syncQueue.size,
      },
    );
    // Note: syncQueue is NOT cleared - we keep tracking mismatches
  }

  /**
   * Runs a single sync cycle: checks for out-of-sync queues and syncs them.
   * Called periodically by the sync interval.
   */
  private async runSyncCycle(): Promise<void> {
    if (!this.enabled) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - manager not enabled',
      );
      return;
    }

    if (this.processing) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - already processing',
      );
      return;
    }

    if (this.isOnCooldown()) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - on cooldown',
        {
          cooldownUntilMs: this.cooldownUntilMs,
          consecutiveFailedCycles: this.consecutiveFailedCycles,
          queueSize: this.syncQueue.size,
        },
      );
      return;
    }

    const outOfSyncCount = this.syncQueue.size;
    if (outOfSyncCount === 0) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - no out-of-sync queues',
      );
      return;
    }

    this.logger.debug('[QueueSessionSyncManager] Starting sync cycle', {
      outOfSyncQueueCount: outOfSyncCount,
    });

    await this.processSyncQueue();

    this.logger.debug('[QueueSessionSyncManager] Sync cycle completed', {
      remainingOutOfSync: this.syncQueue.size,
    });
  }

  private isOnCooldown(now = Date.now()): boolean {
    return this.cooldownUntilMs !== null && now < this.cooldownUntilMs;
  }

  private async processSyncQueue(): Promise<void> {
    if (this.processing) return;
    this.processing = true;
    this.abortController = new AbortController();
    this.cycleAttempted = new Set();
    this.cycleFailed = new Set();
    const startedAt = Date.now();

    // Snapshot at cycle entry: only these queueIds will be processed this cycle.
    // Items added via checkAndEnqueue during the cycle stay in syncQueue and are
    // picked up by the next eligible cycle. Bounds probe size during incidents.
    const snapshot = Array.from(this.syncQueue.values());

    try {
      for (let i = 0; i < snapshot.length; i += this.config.maxConcurrent) {
        if (this.abortController.signal.aborted) break;

        const batch = snapshot
          .slice(i, i + this.config.maxConcurrent)
          .filter((item) => this.syncQueue.has(item.queueId));
        if (batch.length === 0) continue;

        await Promise.allSettled(
          batch.map((item, j) =>
            this.syncQueueItem(item, j * this.config.staggerDelayMs),
          ),
        );

        const more = i + this.config.maxConcurrent < snapshot.length;
        if (more && !this.abortController.signal.aborted && this.enabled) {
          await new Promise((r) => setTimeout(r, this.config.batchCooldownMs));
        }
      }
    } finally {
      const wasAborted = this.abortController?.signal.aborted ?? false;
      this.processing = false;
      this.abortController = null;
      if (this.cycleAttempted.size > 0) {
        this.lastCycleStats = {
          attempted: this.cycleAttempted.size,
          failed: this.cycleFailed.size,
          succeeded: this.cycleAttempted.size - this.cycleFailed.size,
          durationMs: Date.now() - startedAt,
        };
        if (wasAborted) {
          this.logger.debug(
            '[QueueSessionSyncManager] Cycle aborted - skipping cooldown evaluation',
            { ...this.lastCycleStats },
          );
        } else {
          const { attempted, failed } = this.lastCycleStats;
          const allFailed = failed === attempted;
          if (failed >= this.config.failureCountThreshold || allFailed) {
            this.consecutiveFailedCycles += 1;
            const raw =
              this.config.initialBackoffMs *
              Math.pow(
                this.config.backoffMultiplier,
                this.consecutiveFailedCycles - 1,
              );
            const cooldownMs = Math.min(raw, this.config.maxBackoffMs);
            this.cooldownUntilMs = Date.now() + cooldownMs;
          } else {
            this.consecutiveFailedCycles = 0;
            this.cooldownUntilMs = null;
          }
          this.logger.info('[QueueSessionSyncManager] Cycle complete', {
            ...this.lastCycleStats,
            consecutiveFailedCycles: this.consecutiveFailedCycles,
            cooldownUntilMs: this.cooldownUntilMs,
          });
        }
      }
    }
  }

  private async syncQueueItem(
    item: QueueSyncItem,
    delayMs: number,
  ): Promise<void> {
    if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));

    const syncItem = this.syncQueue.get(item.queueId);
    if (!syncItem || this.abortController?.signal.aborted || !this.enabled) {
      return;
    }

    syncItem.status = 'syncing';
    this.cycleAttempted.add(item.queueId);

    try {
      const { sessions } = await getQueueSessions(
        item.queueId,
        this.abortController?.signal,
      );

      const queue = serviceState.queueById[item.queueId];
      if (queue) {
        queue.sessions = sessions;
        this.logger.debug(
          '[QueueSessionSyncManager] Successfully synced queue',
          {
            queueId: item.queueId,
            sessionCount: sessions.length,
          },
        );
      } else {
        this.logger.debug(
          '[QueueSessionSyncManager] Queue not found for session update',
          { queueId: item.queueId },
        );
      }
    } catch (error) {
      this.cycleFailed.add(item.queueId);
      this.logger.error('[QueueSessionSyncManager] Failed to sync queue', {
        queueId: item.queueId,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      // Single attempt — Apollo's retryLink does the retrying.
      this.syncQueue.delete(item.queueId);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current sync queue size (for debugging/monitoring)
   */
  getQueueSize(): number {
    return this.syncQueue.size;
  }

  /**
   * Get sync item status (for debugging/monitoring)
   */
  getSyncItem(queueId: string): QueueSyncItem | undefined {
    return this.syncQueue.get(queueId);
  }

  getLastCycleStats(): {
    attempted: number;
    succeeded: number;
    failed: number;
    durationMs: number;
  } | null {
    return this.lastCycleStats;
  }

  getCooldownUntilMs(): number | null {
    return this.cooldownUntilMs;
  }

  getConsecutiveFailedCycles(): number {
    return this.consecutiveFailedCycles;
  }
}

export const queueSessionSyncManager = new QueueSessionSyncManager();
